import "./ProjectNotFoundComponent.scss";
import HeadingComponent from "@/components/common-components/heading/HeadingComponent";
import { Button } from "@/components/ui/button.tsx";
import { PROJECTS_ROUTE } from "@/constants/RoutesConfig";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from 'react';
import ChipComponent from "@/components/ui/ChipComponent.tsx";
import FloatingIconsRendererComponent from "@/components/common-components/floating-icons-renderer/FloatingIconsRendererComponent.tsx";
import FooterComponent from "@/components/common-components/footer/FooterComponent.tsx";

interface ProjectNotFoundComponentProps {
    projectId?: string;
}

const ProjectNotFoundComponent = ({ projectId }: ProjectNotFoundComponentProps) => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);
    const [isNavigating, setIsNavigating] = useState(false);

    const handleNavigation = useCallback(async () => {
        if (isNavigating) return; // Prevent multiple navigations
        setIsNavigating(true);
        // Start the exit animation
        setTimeout(() => {
            navigate(PROJECTS_ROUTE);
        }, 400); // Match the duration of the exit animation
    }, [navigate, isNavigating]);

    useEffect(() => {
        // Set up countdown timer
        if (countdown > 0 && !isNavigating) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);

            return () => clearTimeout(timer);
        } else if (countdown === 0) {
            handleNavigation();
        }
    }, [countdown, isNavigating, handleNavigation]);

    return (
        <>
            <div className="min-h-[calc(100vh-100px)] w-full overflow-hidden flex flex-direction-column items-center justify-center">
                <motion.main
                    initial={{ opacity: 0, scale: 0.95, filter: "blur(0px)" }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="flex flex-col items-center justify-center w-full py-8"
                >
                    <ChipComponent text={'Project Not Found'} />
                    <HeadingComponent>
                        <span className="text-primary">Oops!</span> This project doesn't exist
                    </HeadingComponent>
                    <div className="text-secondary-foreground mb-8 mt-[-18px] text-[18px] text-center">
                        {projectId ? (
                            <>
                                Project <span className="text-primary font-mono">{projectId}</span> could not be found.
                                <br />
                                <span className="text-secondary">Let's get you back to your projects!</span>
                            </>
                        ) : (
                            <>
                                It seems like this project isn't available anymore.
                                <br />
                                <span className="text-secondary">Let's get you back on track!</span>
                            </>
                        )}
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="flex justify-center"
                    >
                        <Button
                            onClick={handleNavigation}
                            disabled={isNavigating}
                            className="relative"
                        >
                            <span>Back to Projects</span>
                            <span className="font-semBold">{countdown}</span>
                        </Button>
                    </motion.div>
                </motion.main>
                <FloatingIconsRendererComponent />
            </div>
            <FooterComponent />
        </>
    );
};

export default ProjectNotFoundComponent;
